// Alles, was nicht in die Tableiste passt.
//
// Inhaltlich genau das, was am Schreibtisch am Fuß der Seitenleiste steht:
// wer man ist, die restlichen Menüpunkte, Darstellung, Sprache, Abmelden.
// Nur untereinander statt gedrängt, weil hier der ganze Bildschirm zur
// Verfügung steht.
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../gemeinsam/lib/AuthContext';
import { useSprache, SPRACHEN } from '../gemeinsam/lib/i18n';
import type { SprachCode } from '../gemeinsam/lib/i18n';
import { mehrGruppen, tableiste } from '../gemeinsam/lib/navigation';
import { ANBIETER_URL, POWERED_BY, PRODUKT } from '../gemeinsam/lib/marke';
import Icon from '../gemeinsam/komponenten/Icon';
import { ThemaSchalter } from '../gemeinsam/komponenten/Layout';
import { rolleVon } from './Tableiste';

function Reihe({ symbol, children, onClick, ton }: {
  symbol?: Parameters<typeof Icon>[0]['name'];
  children: React.ReactNode;
  onClick?: () => void;
  ton?: 'gefahr';
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-3 text-start text-[15px] active:bg-slate-100 dark:active:bg-slate-800 ${
        ton === 'gefahr' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-slate-100'}`}
    >
      {symbol && (
        <span className="grid size-[30px] shrink-0 place-items-center rounded-[9px] bg-marke-50 text-marke-600 dark:bg-marke-950/60 dark:text-marke-400">
          <Icon name={symbol} groesse={17} />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {!ton && <span className="text-slate-300 dark:text-slate-600" aria-hidden>›</span>}
    </button>
  );
}

function Block({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-4 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white divide-y divide-slate-200 dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
      {children}
    </div>
  );
}

function Gruppentitel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-6 pb-1.5 pt-5 text-[11.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
      {children}
    </p>
  );
}

export default function MehrSeite() {
  const { benutzer, mandant, flags, istOwner, istAdmin, abmelden } = useAuth();
  const { t, sprache, setSprache } = useSprache();
  const navigate = useNavigate();

  const rolle = rolleVon(istOwner, istAdmin);
  const inLeiste = tableiste(rolle, flags).map(e => e.pfad);
  const gruppen = mehrGruppen(rolle, flags, inLeiste);

  return (
    <>
      {/* Den Seitenkopf liefert der Rahmen in Layout.tsx — hier noch einen zu
          setzen, ergäbe zwei Leisten übereinander. */}
      <div className="pb-6">
        <h2 className="px-5 pb-1 pt-3.5 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          {t('nav.mehr')}
        </h2>
        <Gruppentitel>{t('app.konto')}</Gruppentitel>
        <Block>
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-marke-600 text-base font-semibold text-white">
              {(benutzer?.name ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0">
              <span className="block truncate font-semibold text-slate-900 dark:text-slate-100">
                {benutzer?.name}
              </span>
              <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                {istOwner ? t('allg.rolle.inhaber')
                  : istAdmin ? t('allg.rolle.admin')
                    : t('allg.rolle.mitarbeiter')} · {mandant?.name}
              </span>
            </span>
          </div>
        </Block>

        {gruppen.map(g => (
          <div key={g.titel}>
            <Gruppentitel>{t(g.titel)}</Gruppentitel>
            <Block>
              {g.eintraege.map(e => (
                <Reihe key={e.pfad} symbol={e.symbol} onClick={() => navigate(e.pfad)}>
                  {t(e.schluessel)}
                </Reihe>
              ))}
            </Block>
          </div>
        ))}

        <Gruppentitel>{t('app.darstellung')}</Gruppentitel>
        <Block>
          <div className="px-4 py-3">
            <ThemaSchalter />
          </div>
          <label className="flex items-center gap-3 px-4 py-3">
            <span className="grid size-[30px] shrink-0 place-items-center rounded-[9px] bg-marke-50 text-marke-600 dark:bg-marke-950/60 dark:text-marke-400">
              <Icon name="sprache" groesse={17} />
            </span>
            <span className="flex-1 text-[15px] text-slate-900 dark:text-slate-100">
              {t('allg.sprache')}
            </span>
            <select
              value={sprache}
              onChange={e => setSprache(e.target.value as SprachCode)}
              aria-label={t('allg.sprache')}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            >
              {SPRACHEN.map(s => <option key={s.code} value={s.code}>{s.flagge} {s.name}</option>)}
            </select>
          </label>
        </Block>

        <Block>
          <Reihe ton="gefahr" onClick={abmelden}>{t('aktion.abmelden')}</Reihe>
        </Block>

        <p className="px-6 pt-5 text-center text-[11.5px] leading-relaxed text-slate-400 dark:text-slate-500">
          {PRODUKT} © {new Date().getFullYear()}
          <a href={ANBIETER_URL} target="_blank" rel="noopener noreferrer"
             className="mt-0.5 block hover:underline">{POWERED_BY}</a>
        </p>
      </div>
    </>
  );
}
